import React, { useState, useRef, useEffect } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  threshold?: number;
  priority?: boolean;
  sizes?: string;
  preload?: boolean;
  quality?: number;
  backgroundColor?: string;
}

const LazyImage: React.FC<LazyImageProps> = ({ 
  src, 
  alt, 
  className = "", 
  placeholder = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3Crect width='1' height='1' fill='%23f3f4f6'/%3E%3C/svg%3E",
  threshold = 0.1,
  priority = false,
  sizes = "100vw",
  preload = false,
  quality = 85,
  backgroundColor = '#f3f4f6'
}) => {
  const [isLoaded, setIsLoaded] = useState(priority); // Priority images start as loaded
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Convert to WebP if available
  const getWebPSrc = (originalSrc: string) => {
    if (originalSrc.includes('.webp')) return originalSrc;
    return originalSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  };

  // Generate responsive srcset for WebP
  const generateSrcSet = (originalSrc: string, quality: number = 85) => {
    const baseSrc = getWebPSrc(originalSrc);
    
    // For now, return the original since we don't have multiple sizes generated
    // In a production setup, you'd have different sized versions
    return baseSrc;
  };

  const webpSrc = getWebPSrc(src);
  const srcSet = generateSrcSet(src, quality);

  // Preload critical images
  useEffect(() => {
    if (preload && src && priority) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = webpSrc;
      link.type = 'image/webp';
      
      // Check if link already exists
      const existingLink = document.head.querySelector(`link[href="${webpSrc}"]`);
      if (!existingLink) {
        document.head.appendChild(link);
      }
      
      return () => {
        if (link.parentNode) {
          document.head.removeChild(link);
        }
      };
    }
  }, [preload, src, webpSrc, priority]);

  useEffect(() => {
    // If priority is true, load immediately
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold,
        rootMargin: '200px 0px' // Load images 200px before they come into view
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [threshold, priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true); // Show placeholder even on error
  };

  // If there's an error, show a fallback
  if (hasError) {
    return (
      <div 
        className={`bg-gray-200 flex items-center justify-center ${className}`}
        style={{ minHeight: '200px' }}
      >
        <span className="text-gray-500 text-sm">Image not available</span>
      </div>
    );
  }

  return (
    <picture>
      {/* WebP version for modern browsers */}
      {isInView && (
        <source 
          srcSet={srcSet} 
          type="image/webp"
          sizes={sizes}
        />
      )}
      {/* Fallback to original format */}
      <img
        ref={imgRef}
        src={isInView ? src : placeholder}
        alt={alt}
        className={`transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${className}`}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? "eager" : "lazy"}
        sizes={isInView ? sizes : undefined}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        style={{ 
          willChange: isLoaded ? 'auto' : 'opacity',
          backgroundColor: backgroundColor 
        }}
      />
    </picture>
  );
};

export default LazyImage;
